import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { useIsMobile } from "@arcadeai/design-system/hooks/use-mobile";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  ChatHistoryError,
  ChatHistoryList,
  DeleteAllAction,
} from "@arcadeai/ui-kit/chat/components/chat-history-list";
import { useChatRuntime } from "@arcadeai/ui-kit/chat/context";
import { Link } from "@tanstack/react-router";
import { HistoryIcon, SquarePenIcon, XIcon } from "lucide-react";
import {
  createContext,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ErrorBoundary } from "@/components/custom/error-boundary";
import { BFF_URL } from "@/lib/const";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

type ChatHistoryPanelContextValue = {
  open: boolean;
  close: () => void;
  toggle: () => void;
};

const ChatHistoryPanelContext = createContext<ChatHistoryPanelContextValue>({
  open: false,
  close: () => undefined,
  toggle: () => undefined,
});

const HISTORY_PANEL_STORAGE_KEY = "dashboard-playground-chat-history-open";

type PlaygroundChatSidebarProps = {
  viewerId: string;
  onNewChat: () => void;
  onDeleteChat: (id: string) => void;
  onDeleteAll: () => void;
};

export function ChatHistoryTrigger() {
  const { toggle } = useContext(ChatHistoryPanelContext);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label="Toggle chat history"
            className="h-9 gap-2 px-2 text-foreground"
            onClick={toggle}
            variant="outline"
          />
        }
      >
        <HistoryIcon className="size-4" />
        <span className="sr-only md:not-sr-only">History</span>
      </TooltipTrigger>
      <TooltipContent side="bottom">Chat history</TooltipContent>
    </Tooltip>
  );
}

function NewChatButton({ onNewChat }: { onNewChat: () => void }) {
  return (
    <Button
      className="h-9 w-full gap-2 text-foreground"
      onClick={onNewChat}
      variant="outline"
    >
      <SquarePenIcon className="size-4" />
      New Chat
    </Button>
  );
}

export function PlaygroundChatSidebar(
  props: PlaygroundChatSidebarProps & { children: ReactNode }
) {
  const { stop } = useChatRuntime();
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const [open, setOpen] = useState(
    () => window.sessionStorage.getItem(HISTORY_PANEL_STORAGE_KEY) === "true"
  );
  const isMobile = useIsMobile();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  const contextValue = useMemo(
    () => ({ open, close, toggle }),
    [open, close, toggle]
  );

  useEffect(() => {
    window.sessionStorage.setItem(HISTORY_PANEL_STORAGE_KEY, String(open));
  }, [open]);

  useEffect(() => {
    if (!(isMobile && open)) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMobile, open]);

  function handlePanelNewChat() {
    props.onNewChat();
    if (isMobile) {
      setOpen(false);
    }
  }

  function handleHistorySelect() {
    stop();
    if (isMobile) {
      setOpen(false);
    }
  }

  const buildItemHref = useCallback(
    (chatId: string) =>
      `/orgs/${organizationId}/projects/${projectId}/playground/chat/${chatId}`,
    [organizationId, projectId]
  );

  const panelClassName = cn(
    "bg-transparent",
    isMobile
      ? "absolute inset-y-0 right-0 z-30 flex w-[min(20rem,calc(100vw-1rem))] max-w-full flex-col transition-transform duration-200 ease-in-out"
      : "flex shrink-0 flex-col overflow-hidden transition-[width] duration-200 ease-in-out",
    isMobile &&
      (open ? "translate-x-0" : "pointer-events-none translate-x-full")
  );

  return (
    <ChatHistoryPanelContext value={contextValue}>
      <div className="relative flex h-full min-h-0 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {props.children}
        </div>

        {isMobile && open ? (
          <button
            aria-label="Close chat history"
            className="absolute inset-0 z-20 bg-background/80 backdrop-blur-sm md:hidden"
            onClick={close}
            type="button"
          />
        ) : null}

        <div
          className={panelClassName}
          data-state={open ? "open" : "closed"}
          style={isMobile ? undefined : { width: open ? 280 : 0 }}
        >
          <div className="flex h-full w-full flex-col px-2 pt-0 pb-2 md:w-[280px]">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border/60 bg-muted/20 pt-3 shadow-xs">
              <div className="flex items-center justify-between gap-2 px-4 pb-4">
                <span className="font-semibold text-sm">History</span>
                <div className="flex items-center gap-1">
                  <DeleteAllAction
                    baseUrl={BFF_URL}
                    onDeleteAll={props.onDeleteAll}
                    viewerId={props.viewerId}
                  />
                  <Button
                    aria-label="Close chat history"
                    className="size-7 shrink-0 md:hidden"
                    onClick={close}
                    size="icon"
                    variant="ghost"
                  >
                    <XIcon className="size-3.5" />
                  </Button>
                </div>
              </div>
              <div className="px-3 pb-5">
                <NewChatButton onNewChat={handlePanelNewChat} />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-1">
                <ErrorBoundary
                  fallback={(_error: Error, reset: () => void) => (
                    <div className="px-4">
                      <ChatHistoryError
                        baseUrl={BFF_URL}
                        onReset={reset}
                        viewerId={props.viewerId}
                      />
                    </div>
                  )}
                >
                  <Suspense
                    fallback={
                      <div className="space-y-5 px-3 py-1">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <Skeleton
                            className="h-8 w-full rounded-md"
                            key={`skeleton-${i.toString()}`}
                          />
                        ))}
                      </div>
                    }
                  >
                    <ChatHistoryList
                      baseUrl={BFF_URL}
                      buildItemHref={buildItemHref}
                      className="flex flex-col gap-5 px-1 pb-4"
                      linkComponent={Link}
                      onDeleteChat={props.onDeleteChat}
                      onItemSelect={handleHistorySelect}
                      viewerId={props.viewerId}
                    />
                  </Suspense>
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ChatHistoryPanelContext>
  );
}
